import React, { useEffect, useState, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import config from '../config';
import MediaSlideshow from '../components/MediaSlideshow';
import Cookies from 'js-cookie';
import error from '../assets/Tr3.jpg';
import ProductsGrid from '../components/ProductsGrid';
import {
    ProductGridSkeleton,
    ProductDetailsSkeleton,
} from '../components/Skeletons';
import { getMediaUrl } from '../utils/urlHelpers'; // Import the URL helper utility
import Notification from '../components/Notification'; // Import Notification component
import ReportModal from '../components/ReportModal'; // Import Report Modal component
import CompleteProfileModal from '../components/CompleteProfileModal'; // Import Complete Profile Modal
import 'swiper/swiper-bundle.css'; // Import Swiper styles
import CustomCarouselV2 from '../components/CustomCarousel'; // Import CustomCarousel for similar products
import '../styles/productDetails-v3.css';
import '../styles/skeletons.css';
import ScrollToTopButton from '../components/ScrollToTopButton';
import { FaTiktok, FaInstagram, FaFacebook } from 'react-icons/fa';
import { handleLikeClick as likeHandler } from '../utils/likeHandler';
import { UserContext } from '../contexts/UserContext';
import { filterActivePromotions, isProductPromoted, getPromotionBadgeClass, getPromotionTypeLabel } from '../utils/promotionUtils';
import ProductService, { invalidateProductFromAllCaches } from '../services/productService';
import moderationService from '../services/moderationService';
// import webMessagingService from '../services/WebMessagingService'; // Commented out - unused
import { useSelector, useDispatch } from 'react-redux';
import { setProducts as setReduxProducts, setLoading as setReduxLoading, setError as setReduxError } from '../slices/productGridSlice';

const ProductDetails = () => {
    const { uuid } = useParams();  // Changed from 'id' to 'uuid'
    const navigate = useNavigate();
    const { redirectToLogin } = useContext(UserContext);
    const [product, setProduct] = useState(null);
    const [products, setProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(true);
    const [userLoading, setUserLoading] = useState(true);
    // const [isOwnerLoading, setIsOwnerLoading] = useState(true); // Commented out - unused
    const [relatedProducts, setRelatedProducts] = useState([]); // Initialize as an empty array
    const [mainCategoryPromotedProducts, setMainCategoryPromotedProducts] = useState([]);
    const [mainCategoryPromotedLoading, setMainCategoryPromotedLoading] = useState(false);
    const [loggedInUser, setLoggedInUser] = useState(null);
    const [pendingAction, setPendingAction] = useState(null); // Track what action user was trying to do
    const [loading, setLoading] = useState(true);
    const [userDetails, setUserDetails] = useState(null); // Add state for user details
    const [notification, setNotification] = useState({ message: '', type: '', header: '', onConfirm: null }); // Add state for notifications
    const [showMenu, setShowMenu] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [likedProducts, setLikedProducts] = useState([]);
    const [isLikeAnimating, setIsLikeAnimating] = useState(false); // Animation state for like button
    const [showCompleteProfileModal, setShowCompleteProfileModal] = useState(false); // State for complete profile modal
    const [descriptionExpanded, setDescriptionExpanded] = useState(false);
    const [specExpanded, setSpecExpanded] = useState(false);
    const [detailsExpanded, setDetailsExpanded] = useState(false);
    const [fullscreenOpen, setFullscreenOpen] = useState(false);
    const [fullscreenIndex, setFullscreenIndex] = useState(0);
    const menuRef = useRef(null);
    const notificationShownRef = useRef(false); // Track if notification was already shown
    const descriptionRef = useRef(null);
    const specRef = useRef(null);
    const dispatch = useDispatch();
    const reduxProducts = useSelector(state => state.productGrid.products);
    const reduxLastFetched = useSelector(state => state.productGrid.lastFetched);

    const fetchLoggedInUser = async () => {
        const token = Cookies.get('authToken');
        if (token) {
            try {
                const response = await fetch(`${config.backendUrl}/users/me/`, {
                    headers: {
                        Authorization: `Token ${token}`
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    setLoggedInUser(data);
                } else {
                    console.error('Échec de la récupération de l\'utilisateur connecté');
                }
            } catch (error) {
                console.error('Une erreur s\'est produite lors de la récupération de l\'utilisateur connecté:', error);
            }
        }
    };

    useEffect(() => {
        // Scroll to top immediately when product UUID changes
        window.scrollTo(0, 0);

        // Validate product UUID parameter
        if (!uuid || uuid === 'undefined') {
            console.error('Invalid product UUID:', uuid);
            setNotification({
                message: 'ID de produit invalide.',
                type: 'error',
                header: 'Erreur de paramètre',
                onConfirm: () => navigate('/dashboard')
            });
            setLoading(false);
            return;
        }

        // Reset all states when product UUID changes
        setProduct(null);
        setRelatedProducts([]);
        setMainCategoryPromotedProducts([]);
        setUserDetails(null);
        setProducts([]);
        setLoading(true);
        setProductsLoading(true);
        setUserLoading(true);
        setMainCategoryPromotedLoading(false);
        notificationShownRef.current = false;

        const fetchProductDetails = async () => {
            try {
                console.log('Fetching product details for UUID:', uuid);
                const result = await ProductService.getProduct(uuid);

                if (result) {
                    console.log('Product fetched successfully:', result.fromCache ? 'from cache' : 'from network');
                    setProduct(result);
                    fetchRelatedProductsByMainCategory(result.main_category_id, result.id);
                    fetchMainCategoryPromotedProducts(result.main_category_id, result.id);
                    fetchUserDetails(result.owner);
                    fetchUserProducts(result.owner, result.id);
                } else {
                    console.error('No product data returned from service');
                    setNotification({
                        message: 'Produit introuvable ou supprimé.',
                        type: 'error',
                        header: 'Produit introuvable',
                        onConfirm: () => navigate('/dashboard')
                    });
                    setProduct(null);
                }
            } catch (error) {
                console.error('Error fetching product details:', error);

                if (error.message === 'PRODUCT_NOT_FOUND') {
                    setNotification({
                        message: 'Ce produit n\'existe pas ou a été supprimé.',
                        type: 'error',
                        header: 'Produit introuvable',
                        onConfirm: () => navigate('/dashboard')
                    });
                    setProduct(null);
                } else {
                    setNotification({
                        message: 'Erreur lors du chargement du produit. Vérifiez votre connexion internet.',
                        type: 'error',
                        header: 'Erreur de chargement'
                    });
                }
            } finally {
                setLoading(false);
            }
        };

        const fetchLoggedInUser = async () => {
            const token = Cookies.get('authToken');
            if (token) {
                try {
                    const response = await fetch(`${config.backendUrl}/users/me/`, {
                        headers: {
                            Authorization: `Token ${token}`
                        }
                    });
                    if (response.ok) {
                        const data = await response.json();
                        setLoggedInUser(data);
                    } else {
                        console.error('Échec de la récupération de l\'utilisateur connecté');
                    }
                } catch (error) {
                    console.error('Une erreur s\'est produite lors de la récupération de l\'utilisateur connecté:', error);
                }
            }
        };

        fetchProductDetails();
        fetchLoggedInUser();
    }, [uuid]);

    const fetchRelatedProductsByMainCategory = async (mainCategoryId, currentProductId) => {
        if (!mainCategoryId) {

            return;
        }

        try {
            const result = await ProductService.getProductsByMainCategory(mainCategoryId, currentProductId, 1, 20);
            
            if (result && result.products) {
                // Apply active promotion filtering
                const activeProducts = filterActivePromotions(result.products);
                
                // Separate promoted and non-promoted products
                const promotedProducts = activeProducts.filter(product => product.promote_type);
                const regularProducts = activeProducts.filter(product => !product.promote_type);

                // Create final array with promoted products mixed in (every third item)
                const finalProducts = [];
                let promotedIndex = 0;
                let regularIndex = 0;

                while (regularIndex < regularProducts.length || promotedIndex < promotedProducts.length) {
                    // Add two regular products
                    for (let i = 0; i < 2 && regularIndex < regularProducts.length; i++) {
                        finalProducts.push(regularProducts[regularIndex]);
                        regularIndex++;
                    }

                    // Add one promoted product if available
                    if (promotedIndex < promotedProducts.length) {
                        finalProducts.push(promotedProducts[promotedIndex]);
                        promotedIndex++;
                    }
                }

                setRelatedProducts(finalProducts);
            } else {
                console.error('No products returned from main category service');
                setRelatedProducts([]);
            }
        } catch (error) {
            console.error('Error fetching related products by main category:', error);
            setRelatedProducts([]);
        }
    };

    const fetchMainCategoryPromotedProducts = async (mainCategoryId, currentProductId) => {
        if (!mainCategoryId) return;
        
        setMainCategoryPromotedLoading(true);
        try {
            const promotedProducts = await ProductService.fetchPromotedProductsByMainCategoryFromNetwork(mainCategoryId);
            const filteredProducts = filterActivePromotions(promotedProducts);
            // Filter out the current product to avoid duplicates
            const finalProducts = filteredProducts.filter(p => p.id !== currentProductId);
            setMainCategoryPromotedProducts(finalProducts);
        } catch (error) {
            console.error('Error fetching main category promoted products:', error);
            setMainCategoryPromotedProducts([]);
        } finally {
            setMainCategoryPromotedLoading(false);
        }
    };

    const fetchUserDetails = async (userId) => {
        try {
            const response = await fetch(`${config.backendUrl}/users/corner/${userId}/`);
            if (response.ok) {
                const data = await response.json();
                setUserDetails(data);
            } else {
                console.error('Failed to fetch user details');
            }
        } catch (error) {
            console.error('An error occurred while fetching user details:', error);
        } finally {
            setUserLoading(false); // Ensure loading state is updated
        }
    };
    // Helper to determine if cache is fresh (10 min)
    const isCacheFresh = () => reduxLastFetched && (Date.now() - reduxLastFetched < 10 * 60 * 1000);

    // Use Redux cache for owner's products if available
    const fetchUserProducts = async (userId, currentProductId) => {
        setProductsLoading(true);
        if (isCacheFresh() && reduxProducts.length > 0) {
            // Filter products by owner and exclude current product
            const ownerProducts = reduxProducts.filter(p => p.owner === userId && p.id !== currentProductId);
            setProducts(ownerProducts);
            setProductsLoading(false);
            return;
        }
        try {
            // Use owner_uuid if available from product.owner_uuid, otherwise fall back to userId
            const ownerIdentifier = product?.owner_uuid || userId;
            const response = await fetch(`${config.backendUrl}/products/user/${ownerIdentifier}/`);
            if (response.ok) {
                const data = await response.json();
                const mappedProducts = (data.results || data || [])
                    .filter(product => product.id !== currentProductId) // Exclude current product
                    .map(product => ({
                    id: product.id,
                    uuid: product.uuid, // ✅ ADD: Include UUID for navigation
                    name: product.name || '',
                    description: product.description || '',
                    price: product.price || 0,
                    currency: product.currency || 'XAF',
                    category_name: product.category_name || (product.category && product.category.name ? product.category.name : ''),
                    city_name: product.city_name || (product.city && product.city.name ? product.city.name : ''),
                    condition: product.condition || '',
                    likes: product.likes || 0,
                    views: product.views || 0,
                    promote_type: product.promote_type || null,
                    negotiable: product.negotiable || false,
                    first_image: product.first_image || (product.images && product.images.length > 0 ? (product.images[0].url || product.images[0].image) : null),
                    images: product.images || [],
                    liked_by: product.liked_by || [],
                    owner_name: product.owner_name || (product.owner && product.owner.name ? product.owner.name : ''),
                    date_created: product.date_created || null,
                }));
                setProducts(mappedProducts);
                // Optionally update Redux cache if this is the logged-in user's products
                if (loggedInUser && userId === loggedInUser.id) {
                    dispatch(setReduxProducts(mappedProducts));
                }
            } else {
                setProducts([]);
                return { results: [], next: null };
            }
        } catch (error) {
            setProducts([]);
            return { results: [], next: null };
        } finally {
            setProductsLoading(false);
        }
    };

    useEffect(() => {
        if (loggedInUser) {
            // Get liked products from all products including the main product
            const allProducts = product ? [product, ...products] : products;
            const liked = allProducts
                .filter(prod => prod.liked_by && prod.liked_by.includes(loggedInUser.id))
                .map(prod => prod.id);
            setLikedProducts(liked);

            // Establish WebSocket connection for product like notifications
            const socket = new WebSocket(`${config.websocketUrl}product_like/${loggedInUser.id}/`);

            socket.onmessage = (event) => {
                const notification = JSON.parse(event.data);
                setNotification({ message: notification.body, type: 'info', header: '' });
            };

            socket.onclose = () => {
                console.error('WebSocket closed unexpectedly');
            };

            return () => {
                socket.close();
            };
        }
    }, [products, loggedInUser, product]); // Add product to dependencies

    useEffect(() => {
        if (!product && !loading && !notificationShownRef.current) {
            notificationShownRef.current = true; // Mark notification as shown
            setNotification({
                message: 'Produit non trouvé',
                type: 'error',
                header: 'Product Error',
                onClose: () => navigate('/products') // Redirect to products list or another appropriate page
            });
        }
    }, [product, loading, navigate]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    useEffect(() => {
        if (notification.message) {
            setShowMenu(false);
        }
    }, [notification]);

    if (loading) {
        return (
            <div className="pd-page">
                <div className="pd-container pd-skeleton-page">
                    <ProductDetailsSkeleton />
                </div>
            </div>
        );
    }

    if (!product) {
        return null; // Return null as the notification will handle the message
    }
    const handleProductClick = (productUuid) => {
        navigate(`/products/view/${productUuid}`);  // Navigate using UUID
    };
    const handlePromoteClick = () => {
        navigate(`/promote-product/${product.uuid}`);  // Use product.uuid
    };
    const handleEditClick = async () => {
        if (!loggedInUser) {
            setNotification({
                message: 'Vous devez être connecté pour modifier ce produit.',
                type: 'warning',
                header: "Modification du produit",
                onConfirm: () => redirectToLogin(window.location.pathname),
                onClose: () => setNotification({ message: '', type: '', header: '' })
            });
            return;
        }

        // Pass product data directly to edit page to avoid unnecessary API call
        navigate(`/edit-post/${product.uuid}`, { 
            state: { 
                productData: product,
                fromProductDetails: true
            } 
        });
    };
    const handleImageError = (e) => {
        console.error('Image failed to load:', e.target.src);
        e.target.src = error; // Set the fallback image
    };
    const handleBoosterClick = () => {
        // Check available boosts - booster field represents available boosts, not used boosts
        const availableBoosts = product.booster || 0;
        
        if (availableBoosts <= 0) {
            // No boosts available, navigate to boost purchase page
            navigate(`/boost-product/${product.uuid}`);  // Use product.uuid
            return;
        }

        // User has available boosts - show confirmation and use one boost for free
        setNotification({
            message: `Vous avez ${availableBoosts} boost${availableBoosts > 1 ? 's' : ''} disponible${availableBoosts > 1 ? 's' : ''}. Voulez-vous booster ce produit?`,
            type: 'warning',
            header: 'Utiliser un Boost',
            onConfirm: async () => {
                try {
                    const token = Cookies.get('authToken');
                    const response = await fetch(`${config.backendUrl}/products/boost-product/${uuid}/`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Token ${token}`
                        },
                        body: JSON.stringify({
                            quantity: 1,
                            use_free_boost: true  // Flag to indicate using available boost
                        })
                    });
                    if (response.ok) {
                        const responseData = await response.json();
                        setNotification({ 
                            message: responseData.message || 'Produit boosté avec succès!', 
                            type: 'success', 
                            header: 'Boost Réussi' 
                        });
                        // Update product with new boost count from server response
                        setProduct((prevProduct) => ({
                            ...prevProduct,
                            booster: responseData.boosts_remaining || (availableBoosts - 1)
                        }));
                    } else {
                        const errorData = await response.json();
                        setNotification({ 
                            message: errorData.error || 'Échec du boost du produit', 
                            type: 'error', 
                            header: 'Erreur de Boost' 
                        });
                    }
                } catch (error) {
                    console.error('Une erreur s\'est produite lors du boost du produit:', error);
                    setNotification({ 
                        message: 'Une erreur s\'est produite lors du boost du produit', 
                        type: 'error', 
                        header: 'Erreur de Boost' 
                    });
                }
            },
            onClose: () => {
                setNotification({ message: '', type: '', header: '' });
            }
        });
    };
    const handleDeleteClick = async () => {
        setNotification({
            message: 'Êtes-vous sûr de vouloir supprimer ce produit? Cette action est irréversible et supprimera toutes les données associées.',
            type: 'warning',
            header: 'Confirmation de suppression',
            onConfirm: async () => {
                try {
                    const token = Cookies.get('authToken');
                    const response = await fetch(`${config.backendUrl}/products/delete/${product.uuid}/`, {
                        method: 'DELETE',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Token ${token}`
                        }
                    });
                    
                    if (response.ok) {
                        const data = await response.json();
                        
                        // Invalidate all caches for this product to prevent 404 errors
                        invalidateProductFromAllCaches(
                            product.id || product.uuid, 
                            loggedInUser?.id || loggedInUser?.uuid
                        );
                        
                        setNotification({ 
                            message: data.message || 'Produit supprimé avec succès!', 
                            type: 'success', 
                            header: 'Suppression réussie' 
                        });
                        setTimeout(() => navigate(`/products/user/${loggedInUser.uuid || loggedInUser.id}`), 2000);
                    } else {
                        const errorData = await response.json();
                        setNotification({ 
                            message: errorData.error || 'Échec de la suppression du produit', 
                            type: 'error', 
                            header: 'Erreur de suppression' 
                        });
                    }
                } catch (error) {
                    setNotification({ 
                        message: 'Une erreur réseau s\'est produite lors de la suppression', 
                        type: 'error', 
                        header: 'Erreur' 
                    });
                }
            },
            onClose: () => {
                navigate(`/products/view/${product.uuid}`);  // Use product.uuid
            }
        });
    };

    const handleReportClick = async () => {
        if (!loggedInUser) {
            redirectToLogin(window.location.pathname);
            return;
        }
        
        // Check if user has already reported this product
        try {
            const result = await moderationService.checkExistingReport('product', product.uuid || product.id);
            
            if (result.exists) {
                // User has already reported this
                const reportDate = new Date(result.report.created_at).toLocaleDateString('fr-FR');
                setNotification({
                    message: `Vous avez déjà signalé cette annonce le ${reportDate}. Notre équipe examinera votre signalement dans un délai de 24 heures.`,
                    type: 'info',
                    header: 'Signalement déjà envoyé',
                });
            } else {
                // No existing report, show the modal
                setShowReportModal(true);
            }
        } catch (error) {
            console.error('Error checking existing report:', error);
            // If check fails, allow user to proceed with report
            setShowReportModal(true);
        }
    };

    const handleReportSubmit = async (reportData) => {
        try {
            // Build the frontend URL for the product
            const productUrl = `${window.location.origin}/product/${product.uuid || product.id}`;
            
            await moderationService.submitReport({
                target_type: 'product',
                target_id: product.uuid || product.id,
                reason: reportData.reason,
                details: reportData.description, // Changed from 'description' to 'details' to match backend model
                target_url: productUrl,
            });
            
            setShowReportModal(false);
            setNotification({
                message: 'Votre signalement a été envoyé avec succès. Notre équipe l\'examinera dans un délai de 24 heures.',
                type: 'success',
                header: 'Signalement envoyé',
            });
        } catch (error) {
            setNotification({
                message: error.message || 'Erreur lors de l\'envoi du signalement. Veuillez réessayer.',
                type: 'error',
                header: 'Erreur',
            });
        }
    };

    const handleDeactivateClick = () => {
        setNotification({
            message: 'Êtes-vous sûr de vouloir désactiver ce produit?',
            type: 'warning',
            header: 'Désactivation du produit',
            onConfirm: async () => {
                try {
                    const token = Cookies.get('authToken');
                    const response = await fetch(`${config.backendUrl}/products/deactivate/${product.uuid}/`, {  // Use product.uuid
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Token ${token}`
                        }
                    });

                    if (response.ok) {
                        setNotification({
                            message: 'Produit désactivé avec succès!',
                            type: 'success',
                            header: 'Succès',
                        });
                        setProduct((prevProduct) => ({
                            ...prevProduct,
                            active: false,
                        }));
                    } else {
                        setNotification({
                            message: 'Échec de la désactivation du produit.',
                            type: 'error',
                            header: 'Erreur',
                        });
                    }
                } catch (error) {
                    setNotification({
                        message: 'Une erreur s\'est produite lors de la désactivation du produit.',
                        type: 'error',
                        header: 'Erreur',
                    });
                }
            },
            onClose: () => {
                setNotification({ message: '', type: '', header: '' });
            },
        });
    };

    const handleActivateClick = () => {
        setNotification({
            message: 'Êtes-vous sûr de vouloir activer ce produit?',
            type: 'warning',
            header: 'Activation du produit',
            onConfirm: async () => {
                try {
                    const token = Cookies.get('authToken');
                    const response = await fetch(`${config.backendUrl}/products/activate/${product.uuid}/`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Token ${token}`
                        }
                    });

                    if (response.ok) {
                        setNotification({
                            message: 'Produit activé avec succès!',
                            type: 'success',
                            header: 'Succès',
                        });
                        setProduct((prevProduct) => ({
                            ...prevProduct,
                            active: true,
                        }));
                    } else {
                        setNotification({
                            message: 'Échec de l\'activation du produit.',
                            type: 'error',
                            header: 'Erreur',
                        });
                    }
                } catch (error) {
                    setNotification({
                        message: 'Une erreur s\'est produite lors de l\'activation du produit.',
                        type: 'error',
                        header: 'Erreur',
                    });
                }
            },
            onClose: () => {
                setNotification({ message: '', type: '', header: '' });
            },
        });
    };

    const handleCallClick = () => {
        if (!loggedInUser) {
            redirectToLogin(window.location.pathname);
            return;
        }

        // Check if user has completed date of birth and sex
        if (!loggedInUser.date_of_birth || !loggedInUser.sex) {
            setPendingAction('call');
            setShowCompleteProfileModal(true);
            return;
        }

        // Validate phone number exists
        if (!product.owner_mobile_number || product.owner_mobile_number.trim() === '') {
            setNotification({
                message: 'Le vendeur n\'a pas fourni de numéro de téléphone. Vous pouvez le contacter via message.',
                type: 'info',
                header: 'Information',
            });
            return;
        }

        // Clean phone number - remove spaces and formatting
        const cleanPhone = product.owner_mobile_number.replace(/\s+/g, '');
        
        // Open phone dialer
        window.location.href = `tel:${cleanPhone}`;
    };

    const handleRepondreClick = async () => {
        if (!loggedInUser) {
            redirectToLogin(window.location.pathname);
            return;
        }

        // Check if user has completed date of birth and sex
        if (!loggedInUser.date_of_birth || !loggedInUser.sex) {
            setPendingAction('message');
            setShowCompleteProfileModal(true);
            return;
        }

        // Simple check - user can't message themselves
        if (product.owner === loggedInUser.id) {
            setNotification({
                message: 'Vous ne pouvez pas contacter le propriétaire de ce produit',
                type: 'warning',
                header: 'Action non autorisée'
            });
            return;
        }

        // Use UUIDs for navigation
        const productIdentifier = product.uuid || product.id;
        const userIdentifier = loggedInUser.uuid || loggedInUser.id;
        const ownerIdentifier = product.owner_uuid || product.owner;
        
        // Pass product details as query parameters to avoid API call in ChatRoom
        const params = new URLSearchParams({
            productName: product.name || '',
            productPrice: product.price?.toString() || '',
            productCurrency: product.currency || 'CDF',
            productImage: product.images?.[0]?.image || product.featured_image || '',
            categoryName: product.category_name || '',
            categoryId: product.category?.toString() || '',
            ownerName: product.owner_name || '',
            ownerPhone: product.owner_mobile_number || ''
        });
        
        navigate(`/messages/chatroom/product/${productIdentifier}/${userIdentifier}/${ownerIdentifier}?${params.toString()}`);
    };

    const handleProfileComplete = async (updatedUser) => {
        // Refresh user data from backend to ensure we have the latest profile info
        await fetchLoggedInUser();
        
        // Execute the pending action
        if (pendingAction === 'call') {
            if (product && product.owner_mobile_number) {
                const cleanPhone = product.owner_mobile_number.replace(/\s+/g, '');
                window.location.href = `tel:${cleanPhone}`;
            }
        } else if (pendingAction === 'message') {
            const productIdentifier = product.uuid || product.id;
            const userIdentifier = updatedUser.uuid || updatedUser.id;
            const ownerIdentifier = product.owner_uuid || product.owner;
            
            const params = new URLSearchParams({
                productName: product.name || '',
                productPrice: product.price?.toString() || '',
                productCurrency: product.currency || 'CDF',
                productImage: product.images?.[0]?.image || product.featured_image || '',
                categoryName: product.category_name || '',
                categoryId: product.category?.toString() || '',
                ownerName: product.owner_name || '',
                ownerPhone: product.owner_mobile_number || ''
            });
            
            navigate(`/messages/chatroom/product/${productIdentifier}/${userIdentifier}/${ownerIdentifier}?${params.toString()}`);
        }
        
        setPendingAction(null);
    };
    const handleOwnerClick = async () => {
        if (!loggedInUser) {
            redirectToLogin(window.location.pathname);
        } else if (product) {
            // Prefer owner_uuid, but fallback to owner ID if UUID not available
            const ownerIdentifier = product.owner_uuid || product.owner;
            if (!ownerIdentifier) {
                console.error('No owner identifier available for product:', product);
                return;
            }
            navigate(`/user/corner/${ownerIdentifier}/`);
        }
    };

    const customHandleLikeClick = async (productId, isLikedByUser) => {
        // Trigger animation
        setIsLikeAnimating(true);
        
        // Handle like for both the main product and products in grids
        await likeHandler({
            productId,
            isLikedByUser,
            loggedInUser,
            setLikedProducts,
            setProducts: (updater) => {
                // Update products array for grids
                setProducts(updater);
                // Also update the main product if it matches
                if (product && product.id === productId) {
                    setProduct(prevProduct => {
                        if (typeof updater === 'function') {
                            const mockProductsArray = [prevProduct];
                            const updatedArray = updater(mockProductsArray);
                            return updatedArray[0] || prevProduct;
                        }
                        return prevProduct;
                    });
                }
            },
            navigate,
            setNotification,
            redirectToLogin,
        });
        
        // Reset animation after delay
        setTimeout(() => setIsLikeAnimating(false), 600);
    };

    const handleNotificationClose = () => {
        setNotification({ message: '', type: '', header: '' });
    };
    const ProfileImage = ({ user }) => {
        if (!user) return null;

        const getUserInitials = (firstName, lastName) => {
            return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
        };

        const getProfileImageUrl = (url) => {
            if (!url) return null;

            // If it's already a full URL (including S3), return it directly
            if (url.startsWith('http')) {
                return url;
            }

            // If it's an S3 URL without http(s), add https://
            if (url.includes('s3.amazonaws.com')) {
                return `https://${url.replace(/^https?:\/\//, '')}`;
            }

            // For local development
            if (url.startsWith('/')) {
                return `${config.backendUrl}${url}`;
            }

            return url;
        };

        if (!user.profile_picture_url) {
            return (
                <div className="profile-initials" title={`${user.first_name} ${user.last_name}`}>
                    {getUserInitials(user.first_name, user.last_name)}
                </div>
            );
        }

        return (
            <img
                src={getProfileImageUrl(user.profile_picture_url)}
                alt={`${user.first_name} ${user.last_name}`}
                className="profile-picture"
                onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) return;
                    const initialsDiv = document.createElement('div');
                    initialsDiv.className = 'profile-initials';
                    initialsDiv.title = `${user.first_name} ${user.last_name}`;
                    initialsDiv.textContent = getUserInitials(user.first_name, user.last_name);
                    e.target.parentNode.appendChild(initialsDiv);
                }}
            />
        );
    };

    // Execute pending action after successful login
    const executePendingAction = async () => {
        if (!pendingAction || !loggedInUser || !product) return;

        switch (pendingAction) {
            case 'reply':
                navigate('/chatroom', { 
                    state: { 
                        productId: product.id,
                        productOwnerId: product.owner,
                        userId: loggedInUser.id 
                    } 
                });
                break;
            case 'owner':
                navigate(`/user/${product.owner_uuid || product.owner}`);
                break;
            case 'call':
                // Execute call after login
                if (product.owner_mobile_number && product.owner_mobile_number.trim() !== '') {
                    const cleanPhone = product.owner_mobile_number.replace(/\s+/g, '');
                    window.location.href = `tel:${cleanPhone}`;
                }
                break;
            case 'message':
                // Execute message/contact after login
                handleRepondreClick();
                break;
            default:
                break;
        }
        
        setPendingAction(null);
    };

    const isLikedByUser = product && likedProducts.includes(product.id);
    const isOwner = loggedInUser && product && product.owner === loggedInUser.id;
    const formattedPrice = product ? new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(parseFloat(product.price)) : '';
    const isPromotedProduct = product ? isProductPromoted(product) : false;
    const promotionBadgeClass = isPromotedProduct ? (getPromotionBadgeClass(product) || 'promotion-badge') : '';
    const promotionBadgeText = isPromotedProduct ? (getPromotionTypeLabel(product) || 'Produit sponsorisé') : '';

    // Show skeleton while loading
    if (loading || !product) {
        return <ProductDetailsSkeleton />;
    }

    return (
        <div className="pd-page">
            <div className="pd-container">
                {/* ---- Two-column wrapper (desktop: media left, info right) ---- */}
                <div className="pd-main">
                {/* ---- Media Section ---- */}
                <div className="pd-media" onDoubleClick={() => { if (product.media && product.media.length > 0) { setFullscreenOpen(true); } }}>
                    {isPromotedProduct && (
                        <div className="pd-promotion-badge">
                            <i className="fas fa-bolt"></i>
                            <span>{promotionBadgeText}</span>
                        </div>
                    )}
                    {product.media && (
                        <MediaSlideshow
                            media={product.media.map(item => ({
                                ...item,
                                url: getMediaUrl(item.url),
                            }))}
                            caption={`${product.currency} ${formattedPrice} - ${product.negotiable ? 'Négociable' : 'Prix fixe'}`}
                            condition={product.condition}
                            product={{
                                ...product,
                                isOwner,
                                isLikedByUser,
                                price: formattedPrice,
                                onLikeToggle: () => customHandleLikeClick(product.id, isLikedByUser)
                            }}
                            onShareUpdate={(shares) => {
                                setProduct(prev => prev ? { ...prev, shares } : prev);
                            }}
                        />
                    )}
                    {/* Stats overlay on media */}
                    <div className="pd-stats-overlay">
                        <span
                            className={`pd-stat-chip clickable ${isLikedByUser ? 'liked' : ''} ${isLikeAnimating ? 'animating' : ''}`}
                            onClick={(e) => { e.stopPropagation(); !isOwner && customHandleLikeClick(product.id, isLikedByUser); }}
                        >
                            <i className={isLikedByUser ? 'fas fa-heart' : 'far fa-heart'}></i> {product.likes || 0}
                        </span>
                        <span className="pd-stat-chip">
                            <i className="fas fa-eye"></i> {product.views || 0}
                        </span>
                        {product.shares > 0 && (
                            <span className="pd-stat-chip">
                                <i className="fas fa-share-alt"></i> {product.shares}
                            </span>
                        )}
                    </div>
                    {!isOwner && (
                        <button className="pd-report-floating" onClick={handleReportClick} title="Signaler cette annonce">
                            <i className="fas fa-flag"></i>
                        </button>
                    )}
                </div>

                {/* ---- Info Column ---- */}
                <div className="pd-info">
                {/* ---- Summary Card ---- */}
                <div className="pd-summary">
                    <div className="pd-summary-top">
                        <div className="pd-summary-left">
                            <div className="pd-title-row">
                                <h1 className="pd-title">{product.name}</h1>
                                {isPromotedProduct && (
                                    <span className="pd-promotion-chip">
                                        <i className="fas fa-bolt"></i> {promotionBadgeText}
                                    </span>
                                )}
                            </div>
                            <div className="pd-price-row">
                                <span className="pd-price">{product.currency} {formattedPrice || product.price}</span>
                                {product.negotiable ? (
                                    <span className="pd-negotiable-badge"><i className="fas fa-handshake"></i> Négociable</span>
                                ) : (
                                    <span className="pd-fixed-badge"><i className="fas fa-tag"></i> Prix fixe</span>
                                )}
                            </div>
                        </div>
                        {/* Seller — top right corner */}
                        <div className="pd-seller-chip" onClick={handleOwnerClick} style={{ cursor: 'pointer' }}>
                            <div className="pd-seller-avatar">
                                {userLoading ? (
                                    <div className="pd-initials">...</div>
                                ) : userDetails ? (
                                    <>
                                        <ProfileImage user={{
                                            first_name: userDetails.first_name,
                                            last_name: userDetails.last_name,
                                            profile_picture_url: userDetails.profile_picture,
                                        }} />
                                        <span className="pd-online-dot"></span>
                                    </>
                                ) : (
                                    <div className="pd-initials">?</div>
                                )}
                            </div>
                            <span className="pd-seller-name">
                                {userDetails ? `${userDetails.first_name}` : product.owner_name?.split(' ')[0]}
                            </span>
                        </div>
                    </div>
                    <div className="pd-meta-row">
                        <span className="pd-meta-item"><i className="fas fa-map-marker-alt"></i> {product.city_name}</span>
                        <span className="pd-meta-item"><i className="fas fa-folder"></i> {product.category_name}</span>
                        <span className="pd-meta-item"><i className="fas fa-box"></i> {product.condition}</span>
                    </div>
                </div>

                {/* ---- Owner Status Bar ---- */}
                {isOwner && (
                    <div className="pd-status-bar">
                        <span className={`pd-live-badge ${product.active ? 'live' : 'offline'}`}>
                            <span className="pd-pulse"></span>
                            {product.active ? 'Live' : 'Offline'}
                        </span>
                        {product.booster > 0 && (
                            <button className="pd-boost-pill" onClick={handleBoosterClick}>
                                <i className="fas fa-rocket"></i>
                                {product.booster} Boost{product.booster > 1 ? 's' : ''}
                            </button>
                        )}
                        {!isProductPromoted(product) && (
                            <button className="pd-promote-pill" onClick={handlePromoteClick}>
                                <i className="fas fa-bullhorn"></i>
                                Promouvoir
                            </button>
                        )}
                    </div>
                )}

                {/* ---- Description Card ---- */}
                <div className="pd-card">
                    <h3 className="pd-card-title"><i className="fas fa-align-left"></i> Description</h3>
                    <div
                        ref={descriptionRef}
                        className={`pd-description ${descriptionExpanded ? 'expanded' : 'collapsed'}`}
                        dangerouslySetInnerHTML={{ __html: product.description }}
                    ></div>
                    {!descriptionExpanded && <div className="pd-description-fade"></div>}
                    <button className="pd-toggle-btn" onClick={() => setDescriptionExpanded(!descriptionExpanded)}>
                        {descriptionExpanded ? 'Voir moins' : 'Voir plus'} <i className={`fas fa-chevron-${descriptionExpanded ? 'up' : 'down'}`}></i>
                    </button>
                </div>

                {/* ---- Specification Card ---- */}
                {product.specification && (
                    <div className="pd-card">
                        <h3 className="pd-card-title"><i className="fas fa-list-ul"></i> Spécifications</h3>
                        <div
                            ref={specRef}
                            className={`pd-description ${specExpanded ? 'expanded' : 'collapsed'}`}
                            dangerouslySetInnerHTML={{ __html: product.specification }}
                        ></div>
                        {!specExpanded && <div className="pd-description-fade"></div>}
                        <button className="pd-toggle-btn" onClick={() => setSpecExpanded(!specExpanded)}>
                            {specExpanded ? 'Voir moins' : 'Voir plus'} <i className={`fas fa-chevron-${specExpanded ? 'up' : 'down'}`}></i>
                        </button>
                    </div>
                )}

                {/* ---- Details (compact 3-line) ---- */}
                <div className="pd-card pd-details-compact">
                    <div className="pd-detail-line">
                        <span className="pd-detail-label">Catégorie</span>
                        <span className="pd-detail-value">{product.category_name}</span>
                    </div>
                    <div className="pd-detail-line">
                        <span className="pd-detail-label">État</span>
                        <span className="pd-detail-value">{product.condition}</span>
                    </div>
                    <div className="pd-detail-line">
                        <span className="pd-detail-label">Quantité</span>
                        <span className="pd-detail-value">{product.quantity}</span>
                    </div>
                    {detailsExpanded && (
                        <>
                            <div className="pd-detail-line">
                                <span className="pd-detail-label">Ville</span>
                                <span className="pd-detail-value">{product.city_name}</span>
                            </div>
                            {product.date_created && (
                                <div className="pd-detail-line">
                                    <span className="pd-detail-label">Publié</span>
                                    <span className="pd-detail-value">
                                        {new Date(product.date_created).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </span>
                                </div>
                            )}
                            <div className="pd-detail-line">
                                <span className="pd-detail-label">Négociable</span>
                                <span className="pd-detail-value">{product.negotiable ? 'Oui' : 'Non'}</span>
                            </div>
                        </>
                    )}
                    <button className="pd-toggle-btn" onClick={() => setDetailsExpanded(!detailsExpanded)}>
                        {detailsExpanded ? 'Voir moins' : 'Voir plus'} <i className={`fas fa-chevron-${detailsExpanded ? 'up' : 'down'}`}></i>
                    </button>
                </div>
                </div>{/* close pd-info */}
                </div>{/* close pd-main */}

                {/* ---- Promoted Products ---- */}
                {mainCategoryPromotedLoading ? (
                    <>
                        <div className="pd-section-header">
                            <h2 className="pd-section-title">Produits Sponsorisés</h2>
                        </div>
                        <ProductGridSkeleton />
                    </>
                ) : mainCategoryPromotedProducts && mainCategoryPromotedProducts.length > 0 ? (
                    <>
                        <div className="pd-section-header">
                            <h2 className="pd-section-title">Produits Sponsorisés</h2>
                        </div>
                        <ProductsGrid
                            products={mainCategoryPromotedProducts}
                            likedProducts={likedProducts}
                            loggedInUser={loggedInUser}
                            handleProductClick={handleProductClick}
                            handleLikeClick={customHandleLikeClick}
                            hasMore={false}
                            fetchMoreData={() => {}}
                            loading={false}
                        />
                    </>
                ) : null}

                {/* ---- Similar Products ---- */}
                {relatedProducts && relatedProducts.length > 0 && (
                    <>
                        <div className="pd-section-header">
                            <h2 className="pd-section-title">Produits Similaires</h2>
                            {relatedProducts.length > 10 && (
                                <button className="pd-view-more" onClick={() => {
                                    if (product.main_category_id) {
                                        navigate(`/categories/${product.main_category_id}/products`);
                                    } else if (product.category_name) {
                                        navigate(`/products/categories/view/${product.category_name}`);
                                    }
                                }}>
                                    Voir plus
                                </button>
                            )}
                        </div>
                        <CustomCarouselV2
                            products={relatedProducts.slice(0, 15)}
                            onProductClick={handleProductClick}
                            handleLikeClick={customHandleLikeClick}
                            loggedInUser={loggedInUser}
                            loadingMore={false}
                        />
                    </>
                )}

                {/* ---- Owner's Other Products ---- */}
                <div className="pd-section-header">
                    <h2 className="pd-section-title">Autres produits du propriétaire</h2>
                </div>
                {productsLoading ? (
                    <ProductGridSkeleton />
                ) : Array.isArray(products) && products.length > 0 ? (
                    <ProductsGrid
                        products={products}
                        likedProducts={likedProducts}
                        loggedInUser={loggedInUser}
                        handleProductClick={handleProductClick}
                        handleLikeClick={customHandleLikeClick}
                        hasMore={false}
                        fetchMoreData={() => {}}
                        loading={false}
                    />
                ) : (
                    <div className="pd-empty">
                        {isOwner ? (
                            <>
                                <p>Vous n'avez pas encore publié d'autres produits.</p>
                                <button className="pd-btn pd-btn-primary" onClick={() => navigate('/add-post')}>
                                    <i className="fas fa-plus"></i> Ajouter un produit
                                </button>
                            </>
                        ) : (
                            <p>Ce propriétaire n'a pas encore publié d'autres produits.</p>
                        )}
                    </div>
                )}
            </div>

            {/* ---- Sticky Footer ---- */}
            <div className="pd-footer">
                <div className="pd-footer-info">
                    <div className="pd-footer-name">{product.name}</div>
                    <div className="pd-footer-price">{product.currency} {formattedPrice}</div>
                </div>
                <div className="pd-footer-actions">
                    {isOwner ? (
                        <>
                            <button className="pd-btn pd-btn-edit" onClick={handleEditClick}>
                                <i className="fas fa-edit"></i> <span className="pd-btn-text">Modifier</span>
                            </button>
                            <div style={{ position: 'relative' }} ref={menuRef}>
                                <button className="pd-btn pd-btn-menu" onClick={() => setShowMenu(!showMenu)}>
                                    <i className="fas fa-ellipsis-h"></i>
                                </button>
                                {showMenu && (
                                    <div className="pd-footer-menu">
                                        <button className="pd-menu-item" onClick={handleBoosterClick}>
                                            <i className="fas fa-rocket"></i>
                                            <span>
                                                {(product.booster || 0) > 0
                                                    ? `Booster (${product.booster} dispo)`
                                                    : 'Recharger Booster'}
                                            </span>
                                        </button>
                                        {product.active ? (
                                            <button className="pd-menu-item" onClick={handleDeactivateClick}>
                                                <i className="fas fa-ban"></i> <span>Désactiver</span>
                                            </button>
                                        ) : (
                                            <button className="pd-menu-item" onClick={handleActivateClick}>
                                                <i className="fas fa-check"></i> <span>Activer</span>
                                            </button>
                                        )}
                                        <button className="pd-menu-item" onClick={handlePromoteClick}>
                                            <i className="fas fa-bullhorn"></i> <span>Sponsoriser</span>
                                        </button>
                                        <button className="pd-menu-item danger" onClick={handleDeleteClick}>
                                            <i className="fas fa-trash"></i> <span>Supprimer</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <button className="pd-btn pd-btn-primary" onClick={handleRepondreClick}>
                                <i className="fas fa-comment-dots"></i> <span className="pd-btn-text">Contacter</span>
                            </button>
                            {product.owner_mobile_number && (
                                <button className="pd-btn pd-btn-call" onClick={handleCallClick}>
                                    <i className="fas fa-phone-alt"></i> <span className="pd-btn-text">Appeler</span>
                                </button>
                            )}
                            <button
                                className={`pd-btn pd-btn-like ${isLikedByUser ? 'liked' : ''} ${isLikeAnimating ? 'animating' : ''}`}
                                onClick={(e) => { e.stopPropagation(); !isOwner && customHandleLikeClick(product.id, isLikedByUser); }}
                                disabled={isLikeAnimating}
                            >
                                <i className={isLikedByUser ? 'fas fa-heart' : 'far fa-heart'}></i>
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Modals */}
            {notification.message && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    header={notification.header}
                    onConfirm={notification.onConfirm}
                    onClose={handleNotificationClose}
                />
            )}
            {showReportModal && (
                <ReportModal
                    show={showReportModal}
                    onClose={() => setShowReportModal(false)}
                    onSubmit={handleReportSubmit}
                    targetType="product"
                    targetName={product?.name}
                />
            )}
            <CompleteProfileModal
                isOpen={showCompleteProfileModal}
                onClose={() => {
                    setShowCompleteProfileModal(false);
                    setPendingAction(null);
                }}
                onComplete={handleProfileComplete}
                requiredFields={['date_of_birth', 'sex']}
            />
            <ScrollToTopButton />

            {/* ---- Fullscreen Image Viewer ---- */}
            {fullscreenOpen && product.media && product.media.length > 0 && (
                <div className="pd-fullscreen" onClick={() => setFullscreenOpen(false)}>
                    <button className="pd-fs-close" onClick={() => setFullscreenOpen(false)}>
                        <i className="fas fa-times"></i>
                    </button>
                    <div className="pd-fs-content" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="pd-fs-arrow pd-fs-prev"
                            onClick={() => setFullscreenIndex((prev) => (prev - 1 + product.media.length) % product.media.length)}
                        >
                            <i className="fas fa-chevron-left"></i>
                        </button>
                        <img
                            src={getMediaUrl(product.media[fullscreenIndex]?.url)}
                            alt={`${product.name} - ${fullscreenIndex + 1}`}
                            className="pd-fs-image"
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <button
                            className="pd-fs-arrow pd-fs-next"
                            onClick={() => setFullscreenIndex((prev) => (prev + 1) % product.media.length)}
                        >
                            <i className="fas fa-chevron-right"></i>
                        </button>
                    </div>
                    <div className="pd-fs-counter">
                        {fullscreenIndex + 1} / {product.media.length}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductDetails;